import { IRecordedInterviewEntry } from 'types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Alert, AlertIcon } from '@chakra-ui/react';
import * as studentActions from '../../../../../store/student/actions';
import * as AuthActions from '../../../../../store/auth/actions';
import { getMyInterviewAPIKey } from '../../../../../utilities/envVariables';
import { IWidgetRef, Widget } from '@myinterview/widget-react';
import { Loading } from '../../../../atoms/loading';
import { ApplicationState } from '../../../../../store';
import { AxiosError } from 'shared-components';
import { toast } from 'react-toastify';
import { checkRecordedInterviewAttempt } from '../../../../../api/api';
import { useSession } from 'shared-components/authentication';

interface IProps {
  interview: IRecordedInterviewEntry;
  interviewHash: string;
  jobId: string;
  questions: Array<any>;
  welcomeTitle: string;
  interviewAttemptsCount: number;
  isExceeded: boolean;
  encryptedQuestions: string;
  isDisabled: boolean;
}

const myInterviewButtonStyle = {
  display: 'none'
};

enum MyInterviewButtonTextTypes {
  START = 'Start interview',
  CONTINUE = 'Continue recording'
}

const MyInterviewWidget: React.FC<IProps> = props => {
  const {
    interview,
    interviewHash,
    jobId,
    questions,
    encryptedQuestions,
    welcomeTitle,
    interviewAttemptsCount,
    isExceeded,
    isDisabled
  } = props;
  const { interviewId } = interview;
  const studentNumber = useSelector((state: ApplicationState) => state.student.core.student_number);
  const studentId = useSelector((state: ApplicationState) => state.student.core.student_id);
  const isWidgetOpened = useSelector(
    (state: ApplicationState) => state.student.offer?.recordedInterviews?.[interviewHash]?.isWidgetOpened
  );
  const { pathway, uni, intakeId, offerId } = useSelector((state: ApplicationState) => state.student.summary);

  const [uploading, setUploading] = useState(false);
  const [uniJobId, setUniJobId] = useState<string>('UNSET');
  const [attemptsCount, setAttemptsCount] = useState(interviewAttemptsCount);
  const hasAttemptedInterview = Object.keys(interview?.attempts ?? {}).length > 0;
  const [buttonText, setButtonText] = useState<MyInterviewButtonTextTypes>(
    hasAttemptedInterview ? MyInterviewButtonTextTypes.CONTINUE : MyInterviewButtonTextTypes.START
  );
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState(false);

  const widgetRef = useRef<IWidgetRef>(null);

  useEffect(() => {
    if (isWidgetOpened && widgetRef) {
      widgetRef?.current?.open();
      setIsLoading(false);
    }
  }, [isWidgetOpened]);

  const apiKey = getMyInterviewAPIKey();

  const dispatch = useDispatch();
  const session = useSession();

  const questionsSerialized = questions?.map(item => ({
    question: item.question as string,
    description: item?.description || '',
    duration: item.duration,
    attempts: (item?.retakes ?? 0) + 1,
    isScored: item?.isScored,
    thinkingTime: item.thinkingTime
  }));

  useEffect(() => {
    if (jobId) {
      setUniJobId(jobId);
    }
    if (Object.keys(interview.attempts ?? {}).length) {
      setButtonText(MyInterviewButtonTextTypes.CONTINUE);
    }
  }, [interview, jobId]);

  const checkTokenIntervalRef: { current?: NodeJS.Timeout } = useRef();

  useEffect(() => {
    if (isWidgetOpened === true && !checkTokenIntervalRef.current)
      checkTokenIntervalRef.current = setInterval(async () => {
        try {
          const idToken = await session.getIdToken();
          const accessToken = await session.getAccessToken();

          await session.appRefreshSession();
          await checkRecordedInterviewAttempt(
            idToken,
            accessToken,
            uni,
            studentNumber,
            {
              interviewHash,
              questions: encryptedQuestions,
              welcomeTitle,
              attemptIndex: attemptsCount
            },
            pathway
          );
        } catch (err) {
          const error = err as AxiosError;
          if (error?.response?.data === 'Refresh Token has been revoked' || error?.response?.status === 409) {
            toast.error(
              `Attention: Multiple CAS Shield sessions detected. To ensure accurate system usage, you have been automatically logged out. Please return to the other session you have open to continue using CAS Shield.`,
              {
                toastId: 'reauthenticateUser'
              }
            );
            await session.onLogout();
            dispatch(AuthActions.logout());
          }
        }
      }, 5 * 1000);

    return () => {
      if (checkTokenIntervalRef.current) {
        clearInterval(checkTokenIntervalRef.current);
        checkTokenIntervalRef.current = undefined;
      }
    };
  }, [isWidgetOpened]);

  const openWidget = () => {
    setIsLoading(true);
    dispatch(
      studentActions.saveRecordedInterviewAttempt({
        interviewHash,
        questions: encryptedQuestions,
        welcomeTitle,
        attemptIndex: attemptsCount
      })
    );
  };

  let btnContent;

  if (error) {
    btnContent = (
      <Alert status="error">
        <AlertIcon />
        There has been a problem. Please reload the page and try again.
      </Alert>
    );
  } else if (isExceeded || isLoading) {
    btnContent = (
      <>
        <Loading show={isLoading} apiCallsInProgress={[]} />
      </>
    );
  } else if (attemptsCount >= 2) {
    btnContent = (
      <Button
        variant="solid"
        size="lg"
        mt={4}
        isDisabled={isDisabled}
        onClick={() => {
          setIsLoading(true);
          dispatch(
            studentActions.exceededStudentRecordedInterview({
              uni,
              interviewHash,
              interviewId,
              studentNumber,
              intakeId,
              offerId,
              pathway
            })
          );
        }}
      >
        {buttonText}
      </Button>
    );
  } else {
    btnContent = (
      <>
        {apiKey && (
          <Button
            variant="solid"
            size="lg"
            mt={4}
            isDisabled={isDisabled}
            onClick={openWidget}
            id="open-myInterview-widget"
          >
            {buttonText}
            <Loading show={uploading && !interview.recorded} apiCallsInProgress={[]} />
          </Button>
        )}
      </>
    );
  }

  return (
    <>
      {btnContent}
      {apiKey && (
        <Widget
          buttonStyle={myInterviewButtonStyle}
          ref={widgetRef}
          disabled={uploading || uniJobId === 'UNSET' || isDisabled}
          config={{
            env: 'production',
            auth: apiKey,
            debug: false,
            onWidgetClicked: () => {
              setButtonText(MyInterviewButtonTextTypes.CONTINUE); // optimistic update
            },
            onFinish: data => {
              setUploading(true);
              dispatch(
                studentActions.saveRecordedInterviewAttempt({
                  interviewHash,
                  videoId: data.video_id,
                  questions: encryptedQuestions,
                  attemptIndex: attemptsCount
                })
              );
            },
            onError: data => {
              setError(true);
              console.error('Error Message:', data);
            },
            onWidgetClose: () => {
              setAttemptsCount(attemptsCount + 1);
              dispatch(studentActions.recordedInterviewIsWidgetOpened({ key: interviewHash, isWidgetOpened: false }));
            },
            minimizable: true,
            hideHeader: false,
            practiceQuestions: [],
            hideIntercom: true,
            disablePractice: true,
            welcomeTitle: interview.interviewName,
            welcomeText: welcomeTitle,
            overlay: '#03B'
          }}
          candidate={{
            // DOCS: If the same email and job_id is provided, it will resume video. This info is not written in MyInterview docs.
            email: `${interviewHash}-${studentNumber}-${interview.hashTimestamp}`,
            username: interview.username ?? studentId,
            reapply: true
          }}
          job={{
            jobID: uniJobId,
            questions: questionsSerialized,
            title: interview.interviewName
          }}
          company={{
            name: '',
            shouldShowWaterMark: false
          }}
          /* @ts-ignore */
          disablePractice={true}
        >
          <style>
            {`
       .myinterview-widget-header__logo,
         .myinterview-widget-welcome-page__top-tips,
         .myinterview-widget-header__job-title,
         .myinterview-widget-header > div > p,
         .myinterview-widget-welcome-page p:not(.myinterview-widget-welcome-page__text) {
           display: none;
         }

         .myinterview-widget-header__company-name {
           visibility: hidden;
         }
     `}
          </style>
        </Widget>
      )}
    </>
  );
};

export default MyInterviewWidget;
